import PropTypes from 'prop-types';
import {
	Box,
	CircularProgress,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { formatDateTime } from '../../../utils/utils';

const DynamicDomainList = ({
	tenantLevel,
	reportId,
	handleReportId,
	configLoading,
	runningEmailDomainSync,
	emailSyncConfig,
}) => {
	return (
		<>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 600,
					fontSize: '0.9rem',
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
				}}
				gutterBottom
			>
				Dynamic Domain List
			</Typography>

			<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
				Input a Salesforce Report Id below to extract domains from email
				addresses associated to contacts records included within the report. The
				domain list will update nightly, and will disable the ability to add any
				domains manually.
			</Typography>

			<Tooltip
				title={
					!tenantLevel ? 'This setting is controlled by your tenant admin' : ''
				}
				placement='top'
				arrow
				slotProps={{
					tooltip: {
						sx: {
							borderRadius: '5px',
						},
					},
				}}
			>
				<Stack direction='row' spacing={2} alignItems='flex-start'>
					<TextField
						label='Salesforce Report ID'
						value={reportId}
						onChange={
							handleReportId ? (e) => handleReportId(e.target.value) : undefined
						}
						placeholder='e.g., 00OXXXXXXXXXXXX'
						sx={{
							flexGrow: 1,
							minWidth: 280,
							...(!tenantLevel && {
								'& .MuiOutlinedInput-root': {
									'& fieldset': {
										borderColor: 'rgba(0, 0, 0, 0.23)',
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(0, 0, 0, 0.23)',
									},
								},
							}),
						}}
						disabled={
							!tenantLevel ||
							configLoading ||
							runningEmailDomainSync ||
							emailSyncConfig?.status === 'running'
						}
					/>
				</Stack>
			</Tooltip>

			{emailSyncConfig && (
				<Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
					{emailSyncConfig.status === 'running' && (
						<CircularProgress size={12} thickness={5} />
					)}

					<Typography variant='caption' color='text.secondary'>
						Current status: {emailSyncConfig.status || 'pending'}
						{emailSyncConfig.last_run_at
							? ` · Last run: ${formatDateTime(emailSyncConfig.last_run_at)}`
							: ''}
						{emailSyncConfig.last_error
							? ` · Last error: ${emailSyncConfig.last_error}`
							: ''}
					</Typography>
				</Box>
			)}
		</>
	);
};
DynamicDomainList.propTypes = {
	tenantLevel: PropTypes.bool.isRequired,
	reportId: PropTypes.string.isRequired,
	handleReportId: PropTypes.func,
	configLoading: PropTypes.bool.isRequired,
	runningEmailDomainSync: PropTypes.bool.isRequired,
	emailSyncConfig: PropTypes.shape({
		status: PropTypes.string,
		last_run_at: PropTypes.string,
		last_error: PropTypes.string,
	}),
};

export default DynamicDomainList;
